// Per-game adapters that turn a raw `breakdown` array (stored as-is from the
// game on game-end) into the shape the Match Stats view renders:
//
//   { series: number[], rounds: [{ label, value }], signedBars?, chartLabels? }
//
//   series      — cumulative score per round, drives the progression line chart.
//   rounds      — per-round profit/loss (or per-dart score), drives the bars.
//   signedBars  — optional; when false the bars show plain magnitudes (counts /
//                 totals) instead of the default signed (+/-) profit/loss.
//   chartLabels — optional; when true each point's cumulative total is labeled
//                 on the chart (used by the flat event-log games).
//   maxChartLabels — optional; caps how many of those labels each line shows
//                 (default 10, see MatchStatsModal).
//
// Supported games: Hold'em/wild-west-poker, Blackjack, Video Poker and Darts
// render the full "Match Stats Type 1" layout (chart + per-round bars). Color
// Sort, Solitaire and Jungle Block are chart-only — they return a cumulative
// `series` and empty `rounds`, so the modal shows just the timeline chart.
// Shark Pool renders the chart plus aggregate stat bars (Balls Sunk, Streak
// Bonus, Time Bonus); Gem Hockey and Mystic Words likewise, with one bar per
// score source.
// Unknown games return null so the modal stays closed — the Match Stats button
// is already gated on `breakdown?.length`.

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

const runningSum = (values) => {
  let sum = 0;
  return values.map((v) => (sum += v));
};

// Sums each event's `value` by its `name`, for the flat event-log games whose
// bars break the final score down by source.
const sumValuesByName = (breakdown) => {
  const sums = {};
  for (const e of breakdown) {
    const name = typeof e?.name === "string" ? e.name : "";
    sums[name] = (sums[name] ?? 0) + num(e?.value);
  }
  return sums;
};

const blackJack = (breakdown) => ({
  rounds: breakdown.map((r, i) => ({
    label: `HAND ${i + 1}`,
    value:
      num(r?.main?.profit) +
      num(r?.luckySum?.profit) +
      num(r?.perfectPair?.profit),
  })),
  series: breakdown.map((r) => num(r?.balance)),
});

const videoPoker = (breakdown) => ({
  rounds: breakdown.map((r, i) => ({
    label: `HAND ${i + 1}`,
    value: num(r?.pl),
  })),
  series: breakdown.map((r) => num(r?.balance)),
});

const wildWestPoker = (breakdown) => ({
  rounds: breakdown.map((r, i) => ({
    label: `HAND ${i + 1}`,
    value:
      num(r?.mainPayout) +
      num(r?.bonusPayout) -
      num(r?.mainStake) -
      num(r?.bonusStake),
  })),
  series: breakdown.map((r) => num(r?.balance)),
});

const darts = (breakdown) => ({
  rounds: breakdown.map((r, i) => ({
    label: `DART ${i + 1}`,
    value: num(r?.score),
  })),
  series: runningSum(breakdown.map((r) => num(r?.score))),
});

// Color Sort, Solitaire and Jungle Block all report a flat, timestamp-ordered
// event log (score gains, penalties, bonus time) rather than discrete rounds.
// `total` is the running cumulative score after each event, so it maps straight
// onto the timeline chart with per-index labels and no bars (chart-only layout).
const cumulativeChartOnly = (breakdown) => ({
  rounds: [],
  series: breakdown.map((e) => num(e?.total)),
  chartLabels: true,
});

// Shark Pool reports a flat event log. We plot the running `total` on the chart
// and aggregate three stat bars from the event names/values:
//   Balls Sunk   — number of "ADD_SCORE_BALL" events.
//   Streak Bonus — number of events that scored a "STREAK COUNT" (streak names
//                  are embedded in the ball events, e.g. "…(STREAK COUNT 2 x 10)").
//   Time Bonus   — total `value` of "ADD_BONUS_TIME" events (0 when none).
// All three are emitted in a fixed order so the two players' bars pair by index.
// Bars are plain magnitudes, not profit/loss, so `signedBars: false`.
const sharkPool = (breakdown) => {
  let ballsSunk = 0;
  let streakCount = 0;
  let timeBonus = 0;
  for (const e of breakdown) {
    const name = typeof e?.name === "string" ? e.name : "";
    if (name.includes("ADD_SCORE_BALL")) ballsSunk += 1;
    if (name.includes("STREAK COUNT")) streakCount += 1;
    if (name.includes("ADD_BONUS_TIME")) timeBonus += num(e?.value);
  }
  return {
    rounds: [
      { label: "BALLS SUNK", value: ballsSunk },
      { label: "STREAK BONUS", value: streakCount },
      { label: "TIME BONUS", value: timeBonus },
    ],
    series: breakdown.map((e) => num(e?.total)),
    signedBars: false,
    chartLabels: true,
  };
};

// Gem Hockey reports a flat event log. We plot the running `total` on the chart
// and break the final score down into one bar per scoring source, summing each
// event's `value` by name:
//   Damage       — "ADD_SCORE_DAMAGE" (one event per shot, 0 on a miss).
//   Boss Bonus   — "ADD_SCORE_BOSS_DEFEATED".
//   Time Bonus   — "ADD_SCORE_TIME_BONUS".
//   Health Bonus — "ADD_SCORE_HEALTH_BONUS".
//   Moves Bonus  — "ADD_SCORE_MOVES_BONUS".
// All five are emitted in a fixed order (0 when absent) so the two players'
// bars pair by index. Bars are plain magnitudes, so `signedBars: false`.
// Games run ~25 damage events, so the chart labels are capped lower than the
// default to keep it readable.
const GEM_HOCKEY_BARS = [
  ["ADD_SCORE_DAMAGE", "DAMAGE"],
  ["ADD_SCORE_BOSS_DEFEATED", "BOSS BONUS"],
  ["ADD_SCORE_TIME_BONUS", "TIME BONUS"],
  ["ADD_SCORE_HEALTH_BONUS", "HEALTH BONUS"],
  ["ADD_SCORE_MOVES_BONUS", "MOVES BONUS"],
];

const gemHockey = (breakdown) => {
  const sums = sumValuesByName(breakdown);
  return {
    rounds: GEM_HOCKEY_BARS.map(([name, label]) => ({
      label,
      value: sums[name] ?? 0,
    })),
    series: breakdown.map((e) => num(e?.total)),
    signedBars: false,
    chartLabels: true,
    maxChartLabels: 5,
  };
};

// Mystic Words reports a flat event log across several puzzles. We plot the
// running `total` on the chart and break the final score down by source.
// Labels match the game's own results screen:
//   Words Found     — "ADD_SCORE_WORD" (one event per word found).
//   Speed Bonus     — "ADD_SCORE_TIME_BONUS" (one per completed puzzle).
//   Wrong Slides    — "ADD_SCORE_MISS" (penalty per wrong guess; 0 while the
//                     score is still 0).
//   Words Not Found — "ADD_SCORE_MISSED_WORD" (penalty per word left unfound
//                     when a puzzle times out).
// Emitted in a fixed order (0 when absent) so the two players' bars pair by
// index. Bars are score totals, not profit/loss, so `signedBars: false`; the
// two penalty totals stay negative.
const MYSTIC_WORDS_BARS = [
  ["ADD_SCORE_WORD", "WORDS FOUND"],
  ["ADD_SCORE_TIME_BONUS", "SPEED BONUS"],
  ["ADD_SCORE_MISS", "WRONG SLIDES"],
  ["ADD_SCORE_MISSED_WORD", "WORDS NOT FOUND"],
];

const mysticWords = (breakdown) => {
  const sums = sumValuesByName(breakdown);
  return {
    rounds: MYSTIC_WORDS_BARS.map(([name, label]) => ({
      label,
      value: sums[name] ?? 0,
    })),
    series: breakdown.map((e) => num(e?.total)),
    signedBars: false,
    chartLabels: true,
  };
};

const ADAPTERS = {
  "black-jack": blackJack,
  "video-poker": videoPoker,
  "wild-west-poker": wildWestPoker,
  "color-sort": cumulativeChartOnly,
  "shark-pool": sharkPool,
  solitaire: cumulativeChartOnly,
  "jungle-block": cumulativeChartOnly,
  "gem-hockey": gemHockey,
  "mystic-words": mysticWords,
  darts,
};

export const SUPPORTED_MATCH_STATS_GAMES = Object.keys(ADAPTERS);

export const isMatchStatsSupported = (gameName) =>
  Boolean(ADAPTERS[gameName]);

export const adaptMatchStats = (gameName, breakdown) => {
  const adapter = ADAPTERS[gameName];
  if (!adapter || !Array.isArray(breakdown) || breakdown.length === 0) {
    return null;
  }
  return adapter(breakdown);
};
